/**
 * A fórmula do efeito de um buff/debuff (techspec-buff-debuff-config): o mesmo parser do servidor
 * (backend Balance/FormulaEfeito.cs). Números, + − × ÷, parênteses e {MAGIA} / {AVAL}. Sem eval: só conta.
 * Cópia idêntica em frontend-admin/src/paineis/formulaEfeito.ts — mudou um, mude os três.
 */
export const VARIAVEIS_FORMULA = ['MAGIA', 'AVAL'] as const;
export type VariavelFormula = typeof VARIAVEIS_FORMULA[number];
export const FORMULA_TAMANHO_MAX = 120;
const PROFUNDIDADE_MAX = 16;

type No =
  | { k: 'n'; v: number }
  | { k: 'v'; nome: VariavelFormula }
  | { k: '-'; a: No }
  | { k: 'b'; op: '+' | '-' | '*' | '/'; a: No; b: No };

export type Formula = { readonly texto: string; readonly raiz: No };
export type ResultadoFormula = { ok: true; formula: Formula } | { ok: false; erro: string };

const cache = new Map<string, ResultadoFormula>();

export function compilarFormula(bruto: string): ResultadoFormula {
  const s = (bruto ?? '').trim();
  const visto = cache.get(s);
  if (visto) return visto;
  let r: ResultadoFormula;
  try {
    if (!s) throw new Error('a fórmula está vazia.');
    if (s.length > FORMULA_TAMANHO_MAX) throw new Error(`a fórmula passa de ${FORMULA_TAMANHO_MAX} caracteres.`);
    let i = 0;
    const olhar = (): string | null => {
      while (i < s.length && /\s/.test(s[i])) i++;
      if (i >= s.length) return null;
      const c = s[i];
      return c === '×' || c === 'x' || c === 'X' ? '*' : c === '÷' ? '/' : c;
    };
    const fator = (p: number): No => {
      if (p > PROFUNDIDADE_MAX) throw new Error('a fórmula tem parênteses demais.');
      const c = olhar();
      if (c == null) throw new Error('a fórmula termina no meio da conta.');
      if (c === '-') { i++; return { k: '-', a: fator(p + 1) }; }
      if (c === '(') {
        i++;
        const d = expr(p + 1);
        if (olhar() !== ')') throw new Error('falta fechar o parêntese.');
        i++;
        return d;
      }
      if (c === '{') {
        const fim = s.indexOf('}', i);
        if (fim < 0) throw new Error("falta fechar a chave '}'.");
        const nome = s.slice(i + 1, fim).trim().toUpperCase();
        if (!(VARIAVEIS_FORMULA as readonly string[]).includes(nome))
          throw new Error(`{${nome}} não existe. Use ${VARIAVEIS_FORMULA.map(v => `{${v}}`).join(' ou ')}.`);
        i = fim + 1;
        return { k: 'v', nome: nome as VariavelFormula };
      }
      if (/[0-9.,]/.test(c)) {
        const ini = i;
        while (i < s.length && /[0-9.,]/.test(s[i])) i++;
        const txt = s.slice(ini, i).replace(/,/g, '.');
        if (!/^(\d+\.?\d*|\.\d+)$/.test(txt)) throw new Error(`'${s.slice(ini, i)}' não é um número.`);
        return { k: 'n', v: Number(txt) };
      }
      throw new Error(`'${c}' não vale na fórmula (posição ${i + 1}). Use números, + − × ÷, parênteses e {MAGIA}.`);
    };
    const termo = (p: number): No => {
      let a = fator(p);
      for (let c = olhar(); c === '*' || c === '/'; c = olhar()) { i++; a = { k: 'b', op: c, a, b: fator(p) }; }
      return a;
    };
    const expr = (p: number): No => {
      let a = termo(p);
      for (let c = olhar(); c === '+' || c === '-'; c = olhar()) { i++; a = { k: 'b', op: c, a, b: termo(p) }; }
      return a;
    };
    const raiz = expr(0);
    const sobra = olhar();
    if (sobra != null) throw new Error(`sobrou '${sobra}' na posição ${i + 1}.`);
    r = { ok: true, formula: { texto: s, raiz } };
  } catch (e) {
    r = { ok: false, erro: e instanceof Error ? e.message : String(e) };
  }
  if (cache.size < 512) cache.set(s, r);
  return r;
}

function avaliar(n: No, vars: Record<VariavelFormula, number>): number {
  switch (n.k) {
    case 'n': return n.v;
    case 'v': return vars[n.nome];
    case '-': return -avaliar(n.a, vars);
    case 'b': {
      const a = avaliar(n.a, vars), b = avaliar(n.b, vars);
      if (n.op === '+') return a + b;
      if (n.op === '-') return a - b;
      if (n.op === '*') return a * b;
      return b === 0 ? NaN : a / b;
    }
  }
}

/** Teto do efeito, igual a MagiasDeEfeito.ValorMax. */
export const EFEITO_VALOR_MAX = 10000;

/**
 * máx(1, trunc(fórmula)), como o servidor. O servidor conta em decimal; o 1e-9 segura o double
 * (0,1 × 30 = 2,9999…). Fórmula inválida, divisão por zero ou estouro: 1.
 */
export function valorDaFormula(texto: string | null | undefined, magia: number, aval = 0): number {
  if (!texto) return 1;
  const r = compilarFormula(texto);
  if (!r.ok) return 1;
  const v = avaliar(r.formula.raiz, { MAGIA: Math.max(0, magia), AVAL: Math.max(0, aval) });
  if (!Number.isFinite(v)) return 1;
  return Math.min(EFEITO_VALOR_MAX, Math.max(1, Math.trunc(v + (v >= 0 ? 1e-9 : -1e-9))));
}

/** O texto para o jogador: {MAGIA} vira o nome da perícia, * e / viram × e ÷. */
export function formulaLegivel(texto: string, nomes: Record<VariavelFormula, string>): string {
  return texto
    .replace(/\{\s*([a-zA-Z]+)\s*\}/g, (_m, n: string) => nomes[n.toUpperCase() as VariavelFormula] ?? n)
    .replace(/\*/g, '×')
    .replace(/\//g, '÷')
    .replace(/\s+/g, ' ')
    .trim();
}
